import { Button } from "@admin/components/atoms/Button";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  deleteFoodControlPointInstance,
  FoodControlPointInstanceDto,
  PromiseResult,
} from "@enos-monorepo/lib";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface DeleteOverduesModalProps extends ModalProps {
  selectedInstances: FoodControlPointInstanceDto[];
}

export function DeleteOverduesModal({
  selectedInstances,
  ...props
}: DeleteOverduesModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isPending, setIsPending] = useState(false);

  const handleSaveClick = async () => {
    setIsPending(true);

    const updatePromises = selectedInstances.map((instance) => {
      if (instance.id) return deleteFoodControlPointInstance(instance.id);
      return null;
    });

    const results = await Promise.allSettled(
      updatePromises.filter((up) => up !== null),
    );

    const successes = results.filter(
      (result) => result.status === PromiseResult.Fulfilled,
    );

    const failures = results.filter(
      (result) => result.status === PromiseResult.Rejected,
    );

    if (successes.length > 0) {
      toast.success(
        t("food.Overdue Delete Success", { length: successes.length }),
      );
    }

    if (failures.length > 0) {
      toast.error(t("food.Overdue Delete Failed", { length: failures.length }));
    }

    queryClient.invalidateQueries({
      queryKey: ["foodControlPoints", selectedInstances[0]?.companyLocationId],
    });
    queryClient.invalidateQueries({
      queryKey: [
        "foodControlPointInstances",
        selectedInstances[0]?.companyLocationId,
      ],
    });
    setIsPending(false);
    props.onClose?.();
  };

  return (
    <Modal
      title={t("food.Delete Overdue Controls")}
      {...props}
      actionButtons={
        <>
          <Button variant="secondary" type="button" onClick={props?.onClose}>
            {t("common.Discard")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isPending}
            loading={isPending}
            onClick={handleSaveClick}
          >
            {t("food.Delete Overdue Controls")}
          </Button>
        </>
      }
    >
      <form>
        <div>{t("food.Delete Overdue Controls Warning")}</div>
      </form>
    </Modal>
  );
}
